import { useEffect, useMemo, useState } from 'react'
import { createFileRoute, isNotFound } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faCalendar, faChevronRight, faListUl } from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import { Footer, HeaderMiddle, Navbar, TopBar } from '../components/SiteChrome'
import { EbookPromo } from '../components/EbookPromo'
import { WhatsAppChannelSection } from '../components/WhatsAppChannelSection'
import { BlogTagChips } from '../components/BlogTagChips'
import { UpstreamUnavailablePage } from '../components/UpstreamUnavailablePage'
import { BLOG_FALLBACK_IMAGE, formatPublishedDate, getStorefrontConfig, toExternalImage } from '../lib/dropify'
import type { StorefrontBlogPost, StorefrontConfig } from '../lib/dropify'
import { getBlogPost, listBlogPosts } from '../lib/blog'
import { whatsAppCtaProps, track } from '../lib/analytics'
import { getBlogBuyingContext } from '../lib/blogBuyingContext'
import { BlogArticleTracker } from '../components/BlogArticleAnalytics'
import { DEFAULT_OG_IMAGE, absoluteUrl, blogMetaDescription, blogMetaTitle, buildBlogPostingSchema, buildBreadcrumbSchema } from '../lib/seo'
import { storefrontResponseHeaders } from '../lib/storefrontResponse'

const WA_NUMBER = '6285704011028'
const RELATED_POST_IMAGE_TRANSFORM = { width: 240, height: 240, smart: true, format: 'webp', quality: 75 } as const
const BLOG_HERO_IMAGE_TRANSFORM = { width: 1400, height: 500, smart: true, format: 'webp', quality: 78 } as const
const BLOG_OG_IMAGE_TRANSFORM = { width: 1200, height: 630, smart: true, format: 'jpeg', quality: 82 } as const

export const Route = createFileRoute('/blog/$id')({
  loader: async ({ params }) => {
    const [config, postResult] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      getBlogPost({ data: params.id })
        .then((post) => ({ post, unavailable: false }))
        .catch((error) => {
          if (isNotFound(error)) throw error
          return { post: null, unavailable: true }
        }),
    ])

    const post = postResult.post
    const relatedTag = post && getBlogBuyingContext(post).relatedTag
    const relatedPosts = relatedTag
      ? await listBlogPosts({ data: { limit: 8, tag: relatedTag } }).catch(() => [])
      : []
    const related = relatedPosts
      .filter((item) => !post || (item.slug !== post.slug && item.id !== post.id))
      .slice(0, 3)

    return { config, post, related, unavailable: postResult.unavailable }
  },
  headers: ({ loaderData }) => storefrontResponseHeaders(loaderData?.unavailable ?? false),
  head: ({ loaderData, params }) => {
    const post = loaderData?.post
    const title = post ? blogMetaTitle(post, 'Blog') : 'Blog Dropify'
    const description = post ? blogMetaDescription(post) : 'Detail artikel sedang disinkronkan. Buka katalog atau hubungi admin Dropify untuk informasi produk grosir.'
    const postPath = `/blog/${post?.slug || post?.id || params.id}`
    const image = absoluteUrl(toExternalImage(post?.featured_image_url, BLOG_OG_IMAGE_TRANSFORM) || DEFAULT_OG_IMAGE)

    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:type', content: 'article' },
        { property: 'og:url', content: absoluteUrl(postPath) },
        { property: 'og:image', content: image },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: image },
        ...(!post ? [{ name: 'robots', content: 'noindex, follow' }] : []),
      ],
      links: [
        { rel: 'canonical', href: absoluteUrl(postPath) },
      ],
      scripts: post ? [
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildBlogPostingSchema(post)),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildBreadcrumbSchema([
            { name: 'Beranda', path: '/' },
            { name: 'Blog', path: '/blog' },
            { name: post.title, path: postPath },
          ])),
        },
      ] : [],
    }
  },
  component: BlogDetailPage,
})

type TocItem = { id: string; text: string; level: number }

const HTML_ENTITIES: Record<string, string> = {
  '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'", '&apos;': "'", '&nbsp;': ' ',
}

function decodeEntities(text: string): string {
  return text
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
    .replace(/&[a-z#0-9]+;/gi, (entity) => HTML_ENTITIES[entity.toLowerCase()] ?? entity)
}

function slugify(text: string): string {
  const slug = text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s-]/gu, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 80)
    .replace(/^-+|-+$/g, '')
  return slug || 'bagian'
}

/**
 * Parses the CMS HTML content, extracts h2/h3 headings for a table of contents,
 * and injects unique `id` anchors into those headings so the TOC links can scroll to them.
 */
function buildTableOfContents(html: string): { html: string; toc: TocItem[] } {
  const toc: TocItem[] = []
  const used = new Set<string>()

  const out = html.replace(/<(h[23])\b([^>]*)>([\s\S]*?)<\/\1>/gi, (match, tag: string, attrs: string, inner: string) => {
    const text = decodeEntities(inner.replace(/<[^>]+>/g, '')).replace(/\s+/g, ' ').trim()
    if (!text) return match

    const existing = /\sid=["']([^"']+)["']/i.exec(attrs)
    const base = existing ? existing[1] : slugify(text)
    let id = base
    let n = 2
    while (used.has(id)) id = `${base}-${n++}`
    used.add(id)

    toc.push({ id, text, level: tag.toLowerCase() === 'h2' ? 2 : 3 })

    const cleanedAttrs = attrs.replace(/\sid=["'][^"']*["']/i, '')
    return `<${tag}${cleanedAttrs} id="${id}">${inner}</${tag}>`
  })

  return { html: out, toc }
}

function TableOfContents({ items, showHeading = true }: { items: TocItem[]; showHeading?: boolean }) {
  const [activeId, setActiveId] = useState<string>(items[0]?.id ?? '')

  useEffect(() => {
    const headings = items
      .map((item) => document.getElementById(item.id))
      .filter((el): el is HTMLElement => el !== null)
    if (headings.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible[0]) setActiveId(visible[0].target.id)
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 },
    )

    headings.forEach((heading) => observer.observe(heading))
    return () => observer.disconnect()
  }, [items])

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    event.preventDefault()
    const target = document.getElementById(id)
    if (!target) return
    target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    setActiveId(id)
    if (window.history.replaceState) window.history.replaceState(null, '', `#${id}`)
  }

  return (
    <nav aria-label="Daftar isi" className="bg-white rounded-xl border border-slate-100 p-5">
      {showHeading && <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">
        <FontAwesomeIcon icon={faListUl} className="w-3.5 h-3.5" />
        Daftar Isi
      </h3>}
      <ul className="space-y-1 text-sm border-l border-slate-100">
        {items.map((item) => {
          const isActive = item.id === activeId
          return (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                onClick={(event) => handleClick(event, item.id)}
                className={[
                  'block border-l-2 -ml-px py-1.5 transition-colors leading-snug',
                  item.level === 3 ? 'pl-7' : 'pl-4',
                  isActive
                    ? 'border-blue-500 text-blue-600 font-semibold'
                    : 'border-transparent text-slate-500 hover:text-slate-900 hover:border-slate-300',
                ].join(' ')}
              >
                {item.text}
              </a>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

function RelatedCard({ post }: { post: StorefrontBlogPost }) {
  const publishedDate = formatPublishedDate(post.published_at)

  return (
    <a href={`/blog/${post.slug || post.id}`} className="group flex gap-4 items-start p-4 bg-white rounded-xl border border-slate-100 hover:border-blue-200 hover:shadow-md transition-all">
      <div className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-slate-100">
        <img
          src={toExternalImage(post.featured_image_url, RELATED_POST_IMAGE_TRANSFORM) || BLOG_FALLBACK_IMAGE}
          alt={post.featured_image_alt || post.title}
          width="240"
          height="240"
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      </div>
      <div className="flex-1 min-w-0">
        {publishedDate && (
          <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-1">
            <FontAwesomeIcon icon={faCalendar} className="w-3 h-3" />
            {publishedDate}
          </div>
        )}
        <h4 className="text-sm font-semibold text-slate-800 leading-snug group-hover:text-blue-600 transition-colors line-clamp-2">
          {post.title}
        </h4>
      </div>
    </a>
  )
}

function BlogDetailPage() {
  const data = Route.useLoaderData()
  const { id } = Route.useParams()
  if (!data.post) {
    return (
      <UpstreamUnavailablePage
        config={data.config}
        path={`/blog/${id}`}
        eyebrow="Artikel sedang diperbarui"
        title="Detail artikel belum dapat dimuat"
        description="Navigasi, katalog, dan kontak WhatsApp tetap tersedia. Coba muat ulang halaman atau lanjutkan ke arsip blog untuk membaca artikel lain."
        backHref="/blog"
        backLabel="Kembali ke arsip blog"
      />
    )
  }
  return <LoadedBlogDetailPage config={data.config} post={data.post} related={data.related} />
}

function LoadedBlogDetailPage({ config, post, related }: {
  config: StorefrontConfig | null
  post: StorefrontBlogPost
  related: StorefrontBlogPost[]
}) {
  const publishedDate = formatPublishedDate(post.published_at)
  const { html: contentHtml, toc } = useMemo(() => buildTableOfContents(post.content || ''), [post.content])
  const buyingContext = getBlogBuyingContext(post)
  const whatsappHref = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(buyingContext.message)}`

  return (
    <div className="min-h-screen bg-white">
      <TopBar config={config} />
      <HeaderMiddle />
      <Navbar />

      <div className="relative bg-slate-900 overflow-hidden" style={{ height: 'min(400px, 60vw)' }}>
        <img
          src={toExternalImage(post.featured_image_url, BLOG_HERO_IMAGE_TRANSFORM) || BLOG_FALLBACK_IMAGE}
          alt={post.featured_image_alt || post.title}
          width="1400"
          height="500"
          className="w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/30 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-end max-w-4xl mx-auto px-4 pb-10">
          <nav className="flex items-center gap-1.5 text-xs text-slate-400 mb-3">
            <a href="/" className="hover:text-white transition-colors">Beranda</a>
            <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3" />
            <a href="/blog" className="hover:text-white transition-colors">Blog</a>
            <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3" />
            <span className="text-white font-medium line-clamp-1">{post.title}</span>
          </nav>
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <BlogTagChips
              tags={post.tags}
              withIcon
              chipClassName="inline-flex min-h-6 items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white transition-colors"
            />
            {publishedDate && (
              <span className="text-slate-400 text-xs flex items-center gap-1">
                <FontAwesomeIcon icon={faCalendar} className="w-3 h-3" /> {publishedDate}
              </span>
            )}
          </div>
          <h1 className="text-3xl font-black text-white leading-tight max-w-3xl">{post.title?.trim() || 'Artikel Blog Dropify'}</h1>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-12">
        <a href="/blog" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-blue-600 transition-colors mb-10">
          <FontAwesomeIcon icon={faArrowLeft} className="w-4 h-4" /> Kembali ke Blog
        </a>

        <div className="flex flex-col lg:flex-row gap-12">
          <article className="flex-1 min-w-0">
            {post.excerpt && (
              <p className="text-base text-slate-600 leading-relaxed mb-8 font-medium border-l-4 border-blue-500 pl-5 italic">
                {post.excerpt}
              </p>
            )}
            <a href={buyingContext.href}
              onClick={() => track('click_internal_link', { link_url: buyingContext.href, link_text: buyingContext.linkLabel, link_location: 'blog_article_intro', article_id: post.id, article_slug: post.slug })}
              className="mb-6 inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-800">
              {buyingContext.linkLabel}<FontAwesomeIcon icon={faChevronRight} className="h-3 w-3" />
            </a>
            {toc.length > 0 && (
              <details className="lg:hidden mb-8 rounded-xl border border-slate-200 bg-white p-4">
                <summary className="cursor-pointer text-sm font-bold uppercase tracking-wider text-slate-600">Daftar isi</summary>
                <div className="mt-3"><TableOfContents items={toc} showHeading={false} /></div>
              </details>
            )}
            {post.content ? (
              <div
                id="blog-article-body"
                className="prose prose-slate max-w-none prose-headings:font-black prose-headings:text-slate-900 prose-headings:scroll-mt-24 prose-a:text-blue-600 prose-img:rounded-xl"
                dangerouslySetInnerHTML={{ __html: contentHtml }}
              />
            ) : (
              <p className="text-slate-700 text-sm leading-relaxed">Konten artikel belum tersedia.</p>
            )}

            <div className="mt-12 bg-blue-50 rounded-2xl p-8 border border-blue-100">
              <h2 className="text-xl font-black text-slate-900 mb-2">Periksa pilihan {buyingContext.category.toLowerCase()} sebelum order</h2>
              <p className="text-sm text-slate-600 mb-5 max-w-xl">
                Bandingkan model yang tersedia, lalu kirim jumlah dan kota tujuan agar admin dapat memeriksa MOQ, stok, dan quotation.
              </p>
              <a
                href={whatsappHref}
                {...whatsAppCtaProps('blog', { cta_placement: 'article_closing', lp_category: buyingContext.category, lead_item_name: buyingContext.leadItemName })}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex max-w-full items-center justify-center gap-2.5 px-4 sm:px-8 py-4 bg-green-700 hover:bg-green-800 text-white text-center text-base font-bold rounded-xl transition-colors shadow-sm"
              >
                <FontAwesomeIcon icon={faWhatsapp} className="w-5 h-5" />
                Minta quotation {buyingContext.category.toLowerCase()}
              </a>
            </div>
          </article>

          <aside className="lg:w-80 flex-shrink-0">
            <div className="sticky top-6 space-y-6">
              {toc.length > 0 && (
                <div className="hidden lg:block">
                  <TableOfContents items={toc} />
                </div>
              )}

              <div className="bg-slate-900 rounded-xl p-6 text-center">
                <img src="/logo80.webp" alt="Dropify" width="56" height="56" loading="lazy" decoding="async" className="h-14 w-14 object-contain mx-auto mb-3" />
                <h4 className="text-white font-bold mb-1">Dropify</h4>
                <p className="text-slate-400 text-xs leading-relaxed mb-4">
                  Distributor grosir berbasis di Surabaya. Pemesanan dilakukan melalui WhatsApp dan dapat dikirim ke berbagai kota di Indonesia.
                </p>
                <a
                  href={whatsappHref}
                  {...whatsAppCtaProps('blog', { cta_placement: 'sidebar', lp_category: buyingContext.category })}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-green-700 hover:bg-green-800 text-white text-xs font-bold rounded-lg transition-colors w-full justify-center"
                >
                  <FontAwesomeIcon icon={faWhatsapp} className="w-4 h-4" />
                  Cek {buyingContext.category.toLowerCase()}
                </a>
              </div>

              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Artikel Terkait</h3>
                {related.length === 0 ? (
                  <a href={buyingContext.href}
                    onClick={() => track('click_internal_link', { link_url: buyingContext.href, link_text: buyingContext.linkLabel, link_location: 'blog_related_fallback', article_id: post.id, article_slug: post.slug })}
                    className="inline-flex min-h-11 items-center gap-1 text-sm font-semibold text-blue-700 hover:text-blue-800">
                    {buyingContext.linkLabel}<FontAwesomeIcon icon={faChevronRight} className="h-3 w-3" />
                  </a>
                ) : (
                  <div className="space-y-3">
                    {related.map((item) => <RelatedCard key={item.id} post={item} />)}
                  </div>
                )}
              </div>
            </div>
          </aside>
        </div>
      </main>

      <BlogArticleTracker post={post} articleElementId="blog-article-body" />
      <WhatsAppChannelSection showSocksPromo={false} />
      <EbookPromo location="blog_post" />
      <Footer config={config} />
    </div>
  )
}
